// The guard savings calculator's arithmetic (docs/13 "Free tools"): a scheduled automation runs
// an agent every time; a pre-execution guard runs a cheap check first and skips the agent when
// there is nothing to do. The guard itself spends no tokens.

export interface GuardInput {
  /** Minutes between runs. */
  everyMinutes: number;
  /** Share of runs that find something to do, 0–100. */
  busyPercent: number;
  /** Tokens an agent spends finding nothing. */
  idleTokens: number;
  /** Tokens an agent spends when there is work. */
  busyTokens: number;
  /** Dollars per million tokens, input and output blended. */
  rate: number;
}

export interface GuardResult {
  runsPerMonth: number;
  busyRuns: number;
  skippedRuns: number;
  costWithout: number;
  costWith: number;
  saved: number;
  savedPercent: number;
}

const MINUTES_PER_MONTH = 30 * 24 * 60;

export function guardSavings(input: GuardInput): GuardResult {
  const runsPerMonth = Math.floor(MINUTES_PER_MONTH / Math.max(1, input.everyMinutes));
  const busyRuns = Math.round((runsPerMonth * Math.min(100, Math.max(0, input.busyPercent))) / 100);
  const skippedRuns = runsPerMonth - busyRuns;
  const cost = (tokens: number) => (tokens * input.rate) / 1_000_000;
  const costWith = cost(busyRuns * input.busyTokens);
  const costWithout = costWith + cost(skippedRuns * input.idleTokens);
  const saved = costWithout - costWith;
  return { runsPerMonth, busyRuns, skippedRuns, costWithout, costWith, saved, savedPercent: costWithout === 0 ? 0 : (saved / costWithout) * 100 };
}
